import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n";
import { api, downloadBlob } from "@/lib/api";
import { Field, Pick } from "@/app/tool/Options";

export default function ExportDialog({ resultId, open, onOpenChange }) {
  const { t, fmtBytes, errMsg } = useI18n();
  const [o, setO] = useState({ fmt: "webp", preset: "web", width: "", quality: "" });
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const w = Number(o.width), q = Number(o.quality);
  const params = { fmt: o.fmt, preset: o.preset, ...(w >= 200 && w <= 8000 ? { width: w } : {}), ...(q >= 40 && q <= 100 ? { quality: q } : {}) };

  useEffect(() => {
    if (!open || !resultId) return;
    setInfo(null);
    const h = setTimeout(() => {
      api.get(`/results/${resultId}/export/info`, { params }).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e)));
    }, 350);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resultId, params.fmt, params.preset, params.width, params.quality]);

  const download = async () => {
    setBusy(true);
    try { await downloadBlob(`/results/${resultId}/export/download`, params); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--line)]" data-testid="export-dialog">
        <DialogHeader><DialogTitle>{t("tool.exportTitle")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("tool.format")}><Pick value={o.fmt} onChange={(v) => setO({ ...o, fmt: v })} options={[["webp", "WebP"], ["jpeg", "JPEG"], ["png", "PNG"]]} testid="export-format-select" /></Field>
          <Field label={t("tool.quality")}><Pick value={o.preset} onChange={(v) => setO({ ...o, preset: v })} options={["web", "high", "original"].map((k) => [k, t(`tool.${k}`)])} testid="export-preset-select" /></Field>
          <Field label={`${t("tool.width")} (${t("common.optional")})`}><Input type="number" min={200} max={8000} value={o.width} onChange={(e) => setO({ ...o, width: e.target.value })} className={o.width && !(w >= 200 && w <= 8000) ? "border-red-500" : ""} data-testid="export-width-input" /></Field>
          {o.fmt !== "png" && <Field label={`${t("tool.advQuality")}`}><Input type="number" min={40} max={100} value={o.quality} onChange={(e) => setO({ ...o, quality: e.target.value })} data-testid="export-quality-input" /></Field>}
        </div>
        <div className="panel p-3 text-sm font-mono mt-2" data-testid="export-file-info">
          {info ? t("tool.fileInfo", { w: info.width, h: info.height, size: fmtBytes(info.size), fmt: info.format.toUpperCase() }) : t("tool.calc")}
        </div>
        <button onClick={download} disabled={!info || busy} className="btn-brand rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="export-download-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}{t("common.download")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
